import cssText from "~/widgets/styles/globals.css?inline";
import { loadAndApplyThemeOverrides } from "./theme";

/**
 * Whether the basket drawer must render outside its `<baluu-basket>`.
 *
 * The drawer is fixed-position with a z-index (DRAWER_ABOVE_OVERLAYS_Z over
 * the overlay hosts), but a z-index only counts inside the drawer's stacking
 * context. An element the page placed itself — the dashboard's basket embed
 * code can be pasted anywhere, e.g. into a header with its own z-index or a
 * transformed container — can sit in one the drawer can't leave, so "Open
 * basket" over a checkout would open it hidden underneath. Placed straight
 * on `<body>` (how Website v2 and `openBasket` put it) it can't, and the
 * drawer stays inside it as before.
 */
export function drawerNeedsBodyRoot(host: Element | null | undefined): boolean {
  return !!host && host.parentElement !== document.body;
}

/**
 * A body-level root for the drawer, styled as its `<baluu-basket>` is: the
 * widgets' CSS (`:root` → `:host`, as index.ts injects it), the school's theme
 * overrides (cached per school, so usually applied at once), and the host's
 * own `--checkout-primary` when the page set one (`primary-color`, written
 * inline by host-brand). No position or z-index on the box itself, so the
 * drawer's fixed layers stack in the page's root stacking context.
 */
export function mountBodyDrawerRoot(host: HTMLElement): { mount: HTMLElement; dispose: () => void } {
  const box = document.createElement("div");
  box.setAttribute("data-baluu-basket-drawer", host.getAttribute("business-id") ?? "");
  const checkoutPrimary = host.style.getPropertyValue("--checkout-primary");
  if (checkoutPrimary) box.style.setProperty("--checkout-primary", checkoutPrimary);
  document.body.appendChild(box);

  const root = box.attachShadow({ mode: "open" });
  const css =
    cssText || (window as unknown as { __BALUU_WIDGETS_CSS__?: string }).__BALUU_WIDGETS_CSS__ || "";
  if (css) {
    const style = document.createElement("style");
    style.textContent = css.replace(/:root\s*\{/g, ":host {");
    root.appendChild(style);
  }
  const themeKey = host.getAttribute("business-slug") || host.getAttribute("business-id");
  if (themeKey) {
    loadAndApplyThemeOverrides(themeKey, root, host.getAttribute("api-url") || undefined).catch((err) =>
      console.warn("[Baluu Basket] Failed to load theme overrides for the drawer:", err)
    );
  }
  const mount = document.createElement("div");
  root.appendChild(mount);
  return { mount, dispose: () => box.remove() };
}
